import {
  AdminConsole,
  AdminPeoplePage,
  AdminPerson,
  AdminPreview,
  AdminRoles,
  AuditChainStatus,
  AuditEntry,
  AuditLogPage,
  AuditTrail,
  ConnectOptions,
  ConnectSuggestedResult,
  Discrepancies,
  LatestSyncPreview,
  SyncPreview,
  GoLiveChecklist,
  PersonAccess,
  SyncIssue,
  SyncRun,
  SyncStatus,
  type AdminPeopleExportQuery,
  type AuditExportQuery,
  type ConnectEntra,
  type ConnectSuggested,
  type DisconnectEntra,
  type GrantableRole,
  type MergePeople,
  type NotSamePerson,
  type RequestSyncPreview,
  type ResolveSyncIssue,
  type RoleHistoryQuery,
  type CreateManualPerson,
  type SetAppFields,
  type SetVisibility,
  type UpdateManualPerson,
} from '@myrewards/contracts';
import {
  BudgetDraft,
  ReleaseShareworksResult,
  ReopenCandidate,
  ReopenResult,
  Rollover,
  StuckClaims,
  YearStatus,
  type ReopenAward,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// The System Admin's area (SYS1–SYS8, APP-044): the console, People, Directory sync, Roles &
// lists, the Audit log, Preview & testing and the data tools. Admin reads are refused while
// previewing (403 `preview_sample_data`); the pages say so instead of showing empty numbers.

const Id = z.uuid();
const enc = (id: string) => encodeURIComponent(Id.parse(id));

function withParams(base: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params))
    if (value !== undefined && value !== '') search.set(key, value);
  const query = search.toString();
  return query === '' ? base : `${base}?${query}`;
}

export const adminKeys = {
  all: ['admin'] as const,
  console: ['admin', 'console'] as const,
  goLive: ['admin', 'go-live'] as const,
  people: ['admin', 'people'] as const,
  peopleList: (filters: PeopleFilters, page: number, size: number) =>
    ['admin', 'people', 'list', filters, page, size] as const,
  person: (id: string) => ['admin', 'people', 'one', id] as const,
  personHistory: (id: string) => ['admin', 'people', 'history', id] as const,
  personAccess: (id: string) => ['admin', 'people', 'access', id] as const,
  roles: ['admin', 'roles'] as const,
  roleHistory: (q: RoleHistoryQuery) => ['admin', 'roles', 'history', q] as const,
};

// --- Console and go-live ----------------------------------------------------------------------

/** SYS1: sync health, people to set up, discrepancies, roles, stuck claims, Preview, AI, go-live. */
export const adminConsoleQuery = queryOptions({
  queryKey: adminKeys.console,
  queryFn: ({ signal }) => request('/api/v1/admin/console', { schema: AdminConsole, signal }),
  staleTime: 30_000,
  retry: false,
});

/** FR5: the 12 steps, worked out from the data on every read. */
export const goLiveQuery = queryOptions({
  queryKey: adminKeys.goLive,
  queryFn: ({ signal }) => request('/api/v1/admin/go-live', { schema: GoLiveChecklist, signal }),
  staleTime: 30_000,
  retry: false,
});

// --- People (SYS6) ------------------------------------------------------------------------------

export type PeopleFilters = Omit<AdminPeopleExportQuery, 'status'> & {
  status: NonNullable<AdminPeopleExportQuery['status']>;
};

function peopleParams(f: PeopleFilters): Record<string, string | undefined> {
  return {
    q: f.q,
    sector: f.sector,
    role: f.role,
    status: f.status === 'all' ? undefined : f.status,
  };
}

/** Which page of a server-paged list, and how many rows it holds. */
export interface PageRequest {
  page: number;
  size: number;
}

/**
 * Everyone under the filters, by name, one numbered page at a time (APP-135). The page being left
 * stays on screen while the next one loads.
 */
export function adminPeopleQuery(filters: PeopleFilters, paging: PageRequest) {
  return queryOptions({
    queryKey: adminKeys.peopleList(filters, paging.page, paging.size),
    queryFn: ({ signal }) =>
      request(
        withParams('/api/v1/admin/people', {
          ...peopleParams(filters),
          page: String(paging.page),
          size: String(paging.size),
        }),
        { schema: AdminPeoplePage, signal },
      ),
    staleTime: 30_000,
    retry: false,
  });
}

/** The People list under the same filters, as CSV (a plain download link; the server audits it). */
export function peopleExportHref(filters: PeopleFilters): string {
  return withParams('/api/v1/admin/people/export', peopleParams(filters));
}

export function adminPersonQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.person(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/people/${enc(id)}`, { schema: AdminPerson, signal }),
    staleTime: 30_000,
    retry: false,
  });
}

export function adminPersonHistoryQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.personHistory(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/people/${enc(id)}/history`, { schema: AuditTrail, signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/** What a person can do and see: roles, groups, lists, views and settings areas (SYS2 side card). */
export function personAccessQuery(id: string) {
  return queryOptions({
    queryKey: adminKeys.personAccess(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/people/${enc(id)}/access`, { schema: PersonAccess, signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/** Every sector, for the sector pickers (the People page carries them for its filter). */
export const adminSectorsQuery = queryOptions({
  queryKey: ['admin', 'people', 'sectors'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/admin/people?size=1', { schema: AdminPeoplePage, signal }),
  select: (page) => page.sectors,
  staleTime: 5 * 60_000,
  retry: false,
});

/** A few people by name, EE# or email, leavers and hidden records included (Directory sync). */
export function adminPeopleSearchQuery(q: string) {
  const term = q.trim();
  return queryOptions({
    queryKey: ['admin', 'people', 'search', term] as const,
    queryFn: ({ signal }) =>
      request(withParams('/api/v1/admin/people', { q: term, size: '8' }), {
        schema: AdminPeoplePage,
        signal,
      }),
    enabled: term.length >= 2,
    staleTime: 30_000,
    retry: false,
  });
}

export function setAppFields(id: string, body: SetAppFields): Promise<AdminPerson> {
  return request(`/api/v1/admin/people/${enc(id)}/app-fields`, {
    method: 'PUT',
    body,
    schema: AdminPerson,
  });
}

export function setOverride(
  id: string,
  field: string,
  body: { value: unknown; reason: string },
): Promise<AdminPerson> {
  return request(`/api/v1/admin/people/${enc(id)}/overrides/${encodeURIComponent(field)}`, {
    method: 'PUT',
    body,
    schema: AdminPerson,
  });
}

export function clearOverride(id: string, field: string, reason: string): Promise<AdminPerson> {
  return request(`/api/v1/admin/people/${enc(id)}/overrides/${encodeURIComponent(field)}`, {
    method: 'DELETE',
    body: { reason },
    schema: AdminPerson,
  });
}

export function setVisibility(id: string, body: SetVisibility): Promise<AdminPerson> {
  return request(`/api/v1/admin/people/${enc(id)}/visibility`, {
    method: 'PUT',
    body,
    schema: AdminPerson,
  });
}

export function createManualPerson(body: CreateManualPerson): Promise<AdminPerson> {
  return request('/api/v1/admin/people', { method: 'POST', body, schema: AdminPerson });
}

export function updateManualPerson(id: string, body: UpdateManualPerson): Promise<AdminPerson> {
  return request(`/api/v1/admin/people/${enc(id)}`, { method: 'PATCH', body, schema: AdminPerson });
}

// --- Directory sync (SYS3) ----------------------------------------------------------------------

export const directoryKeys = {
  status: ['admin', 'directory', 'status'] as const,
  discrepancies: ['admin', 'directory', 'discrepancies'] as const,
  run: (id: string) => ['admin', 'directory', 'run', id] as const,
  connectOptions: (id: string) => ['admin', 'directory', 'connect-options', id] as const,
  preview: ['admin', 'directory', 'preview'] as const,
};

/** The newest preview (APP-113 §2), or null when there has never been one. */
export const latestPreviewQuery = queryOptions({
  queryKey: directoryKeys.preview,
  queryFn: ({ signal }) =>
    request('/api/v1/admin/directory/previews/latest', { schema: LatestSyncPreview, signal }),
  staleTime: 0,
  retry: false,
});

/** Preview with these settings: queued for the worker; whatever isn't given is the saved one. */
export function requestSyncPreview(body: RequestSyncPreview): Promise<SyncPreview> {
  return request('/api/v1/admin/directory/previews', {
    method: 'POST',
    body,
    schema: SyncPreview,
  });
}

/** When the sync last ran, what it found, and a run waiting or under way. */
export const syncStatusQuery = queryOptions({
  queryKey: directoryKeys.status,
  queryFn: ({ signal }) =>
    request('/api/v1/admin/directory/status', { schema: SyncStatus, signal }),
  staleTime: 15_000,
  retry: false,
});

/** Overrides that differ from Entra, people to set up, likely duplicates and open sync issues. */
export function discrepanciesQuery(paging: PageRequest) {
  return queryOptions({
    queryKey: [...directoryKeys.discrepancies, paging.page, paging.size] as const,
    queryFn: ({ signal }) =>
      request(
        withParams('/api/v1/admin/directory/discrepancies', {
          page: String(paging.page),
          size: String(paging.size),
        }),
        { schema: Discrepancies, signal },
      ),
    staleTime: 30_000,
    retry: false,
  });
}

/** The discrepancy report as CSV (a plain link; the server audits the export). */
export const discrepanciesExportHref = '/api/v1/admin/directory/discrepancies/export';

/** Links two records the sync wasn't sure about, or dismisses the issue, with a reason. */
export function resolveSyncIssue(id: string, body: ResolveSyncIssue): Promise<SyncIssue> {
  return request(`/api/v1/admin/directory/issues/${enc(id)}/resolve`, {
    method: 'POST',
    body,
    schema: SyncIssue,
  });
}

/**
 * What can connect this record to Entra (APP-113 §7): accounts the sync holds for it, sign-ins
 * waiting that look like it, likely duplicates with what blocks a merge, and whether an object ID
 * can be typed.
 */
export function connectOptionsQuery(id: string) {
  return queryOptions({
    queryKey: directoryKeys.connectOptions(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/directory/people/${enc(id)}/connect-options`, {
        schema: ConnectOptions,
        signal,
      }),
    staleTime: 0,
    retry: false,
  });
}

export function connectEntra(id: string, body: ConnectEntra): Promise<void> {
  return request(`/api/v1/admin/directory/people/${enc(id)}/connect`, {
    method: 'POST',
    body,
    schema: z.undefined(),
  });
}

export function disconnectEntra(id: string, body: DisconnectEntra): Promise<void> {
  return request(`/api/v1/admin/directory/people/${enc(id)}/disconnect`, {
    method: 'POST',
    body,
    schema: z.undefined(),
  });
}

/** Merge `body.otherId` into the record `keepId`, which is kept. */
export function mergePeople(keepId: string, body: MergePeople): Promise<void> {
  return request(`/api/v1/admin/directory/people/${enc(keepId)}/merge`, {
    method: 'POST',
    body,
    schema: z.undefined(),
  });
}

export function notSamePerson(body: NotSamePerson): Promise<void> {
  return request('/api/v1/admin/directory/duplicates/not-same', {
    method: 'POST',
    body,
    schema: z.undefined(),
  });
}

export function connectSuggested(body: ConnectSuggested): Promise<ConnectSuggestedResult> {
  return request('/api/v1/admin/directory/issues/connect-suggested', {
    method: 'POST',
    body,
    schema: ConnectSuggestedResult,
  });
}

/** Run now: queued for the worker (it never runs in the request). */
export function startSyncRun(): Promise<SyncRun> {
  return request('/api/v1/admin/directory/runs', { method: 'POST', schema: SyncRun });
}

// --- Audit log (SYS4) ---------------------------------------------------------------------------

export type AuditFilters = AuditExportQuery;

function auditParams(f: AuditFilters): Record<string, string | undefined> {
  return {
    area: f.area,
    source: f.source,
    person: f.person,
    // APP-148 D26: what was done on someone's behalf, and for whom.
    forSomeoneElse: f.forSomeoneElse === true ? 'true' : undefined,
    forPerson: f.forPerson,
    // R97: only what the Stock Admin team did for someone, and only what one person did.
    actedFor: f.actedFor === true ? 'true' : undefined,
    actor: f.actor,
    entityType: f.entityType,
    entityId: f.entityId,
    award: f.award,
    q: f.q,
    from: f.from,
    to: f.to,
  };
}

export const auditKeys = {
  all: ['admin', 'audit'] as const,
  list: (f: AuditFilters, page: number, size: number, upTo: number | null) =>
    ['admin', 'audit', 'list', f, page, size, upTo] as const,
  entry: (id: string) => ['admin', 'audit', 'entry', id] as const,
  chain: ['admin', 'audit', 'chain'] as const,
};

/**
 * The log under the filters, newest first, one numbered page at a time. `upTo` is the newest entry
 * page 1 saw: later pages stop there, so new entries don't push rows along while someone reads.
 */
export function auditLogQuery(filters: AuditFilters, paging: PageRequest & { upTo?: number }) {
  return queryOptions({
    queryKey: auditKeys.list(filters, paging.page, paging.size, paging.upTo ?? null),
    queryFn: ({ signal }) =>
      request(
        withParams('/api/v1/admin/audit', {
          ...auditParams(filters),
          page: String(paging.page),
          size: String(paging.size),
          upTo: paging.upTo === undefined ? undefined : String(paging.upTo),
        }),
        { schema: AuditLogPage, signal },
      ),
    staleTime: 30_000,
    retry: false,
  });
}

export function auditEntryQuery(id: string) {
  return queryOptions({
    queryKey: auditKeys.entry(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/audit/${enc(id)}`, { schema: AuditEntry, signal }),
    staleTime: Infinity,
    retry: false,
  });
}

/** The log under the same filters as CSV for Excel (a plain link; the server audits the export). */
export function auditExportHref(filters: AuditFilters): string {
  return withParams('/api/v1/admin/audit/export', auditParams(filters));
}

/** The hash chain's last check, and any check waiting to run. */
export const auditChainQuery = queryOptions({
  queryKey: auditKeys.chain,
  queryFn: ({ signal }) =>
    request('/api/v1/admin/audit/chain', { schema: AuditChainStatus, signal }),
  staleTime: 30_000,
  retry: false,
});

/** Ask for a chain check now; the worker runs it and records the result in the log. */
export function requestChainCheck(): Promise<AuditChainStatus> {
  return request('/api/v1/admin/audit/chain/verify', {
    method: 'POST',
    schema: AuditChainStatus,
  });
}

// --- Roles & lists (SYS2) -------------------------------------------------------------------------

/** The Executives list, one Sector Lead per sector, the role grants and the Tester row. */
export const adminRolesQuery = queryOptions({
  queryKey: adminKeys.roles,
  queryFn: ({ signal }) => request('/api/v1/admin/roles', { schema: AdminRoles, signal }),
  staleTime: 30_000,
  retry: false,
});

/** One list's changes, newest first: who was added or removed, by whom and why. */
export function roleHistoryQuery(q: RoleHistoryQuery) {
  return queryOptions({
    queryKey: adminKeys.roleHistory(q),
    queryFn: ({ signal }) =>
      request(withParams('/api/v1/admin/roles/history', { list: q.list, key: q.key }), {
        schema: AuditTrail,
        signal,
      }),
    staleTime: 30_000,
    retry: false,
  });
}

const why = (reason: string) => (reason.trim() === '' ? {} : { reason: reason.trim() });
const sectorPath = (sectorId: string) =>
  `/api/v1/admin/roles/sector-leads/${encodeURIComponent(z.string().min(1).max(100).parse(sectorId))}`;

export function addExecutive(personId: string, reason: string): Promise<AdminRoles> {
  return request(`/api/v1/admin/roles/executives/${enc(personId)}`, {
    method: 'PUT',
    body: why(reason),
    schema: AdminRoles,
  });
}

export function removeExecutive(personId: string, reason: string): Promise<AdminRoles> {
  return request(`/api/v1/admin/roles/executives/${enc(personId)}`, {
    method: 'DELETE',
    body: why(reason),
    schema: AdminRoles,
  });
}

/** Names the sector's one Sector Lead; the delegates the last one named stop with them. */
export function setSectorLead(
  sectorId: string,
  personId: string,
  reason: string,
): Promise<AdminRoles> {
  return request(sectorPath(sectorId), {
    method: 'PUT',
    body: { personId, ...why(reason) },
    schema: AdminRoles,
  });
}

export function clearSectorLead(sectorId: string, reason: string): Promise<AdminRoles> {
  return request(sectorPath(sectorId), {
    method: 'DELETE',
    body: why(reason),
    schema: AdminRoles,
  });
}

export function grantRole(
  role: GrantableRole,
  personId: string,
  reason: string,
): Promise<AdminRoles> {
  return request(`/api/v1/admin/roles/grants/${role}/${enc(personId)}`, {
    method: 'PUT',
    body: why(reason),
    schema: AdminRoles,
  });
}

export function revokeRole(
  role: GrantableRole,
  personId: string,
  reason: string,
): Promise<AdminRoles> {
  return request(`/api/v1/admin/roles/grants/${role}/${enc(personId)}`, {
    method: 'DELETE',
    body: why(reason),
    schema: AdminRoles,
  });
}

// --- Preview & testing (SYS8) ---------------------------------------------------------------------

export const previewKeys = { admin: ['admin', 'preview'] as const };

/** Allow Preview as, the people who can preview and the recent sessions (with their sector). */
export const adminPreviewQuery = queryOptions({
  queryKey: previewKeys.admin,
  queryFn: ({ signal }) => request('/api/v1/admin/preview', { schema: AdminPreview, signal }),
  staleTime: 15_000,
  retry: false,
});

/** Go live (FR5): every required step done; records the launch date and queues the welcome. */
export function launch(reason: string): Promise<GoLiveChecklist> {
  return request('/api/v1/admin/go-live', {
    method: 'POST',
    body: { reason },
    schema: GoLiveChecklist,
  });
}

// --- Support tools (SYS1) -------------------------------------------------------------------------

export const supportKeys = {
  claims: ['admin', 'support', 'claims'] as const,
  award: (displayId: string) => ['admin', 'support', 'award', displayId] as const,
};

const DisplayId = z.string().trim().min(1).max(40);
const awardPath = (displayId: string) =>
  `/api/v1/admin/support/awards/${encodeURIComponent(DisplayId.parse(displayId))}`;

/** Claims held past the timeout: Shareworks rows to release, award claims anyone may take. */
export const supportClaimsQuery = queryOptions({
  queryKey: supportKeys.claims,
  queryFn: ({ signal }) => request('/api/v1/admin/support/claims', { schema: StuckClaims, signal }),
  staleTime: 30_000,
  retry: false,
});

export function releaseShareworksClaims(
  claimIds: readonly string[],
  reason: string,
): Promise<ReleaseShareworksResult> {
  return request('/api/v1/admin/support/claims/release', {
    method: 'POST',
    body: { claimIds, reason },
    schema: ReleaseShareworksResult,
  });
}

/** A closed award by its display ID, and whether it can be reopened. */
export function reopenCandidate(displayId: string): Promise<ReopenCandidate> {
  return request(awardPath(displayId), { schema: ReopenCandidate });
}

export function reopenAward(displayId: string, body: ReopenAward): Promise<ReopenResult> {
  return request(`${awardPath(displayId)}/reopen`, {
    method: 'POST',
    body,
    schema: ReopenResult,
  });
}

// --- Year rollover --------------------------------------------------------------------------------

export const rolloverKeys = {
  rollover: ['admin', 'rollover'] as const,
  draft: (year: number) => ['budgets', 'draft', year] as const,
};

/** Next year's three steps: the budgets draft, the settings review, archiving this year. */
export const rolloverQuery = queryOptions({
  queryKey: rolloverKeys.rollover,
  queryFn: ({ signal }) => request('/api/v1/admin/rollover', { schema: Rollover, signal }),
  staleTime: 30_000,
  retry: false,
});

export function budgetDraftQuery(fiscalYear: number) {
  return queryOptions({
    queryKey: rolloverKeys.draft(fiscalYear),
    queryFn: ({ signal }) =>
      request(`/api/v1/budgets/drafts?fiscalYear=${String(fiscalYear)}`, {
        schema: BudgetDraft,
        signal,
      }),
    staleTime: 30_000,
    retry: false,
  });
}

export function copyBudgets(
  fiscalYear: number,
  fromYear: number,
  reason: string,
): Promise<BudgetDraft> {
  return request('/api/v1/budgets/drafts/copy', {
    method: 'POST',
    body: { fiscalYear, fromYear, ...(reason.trim() !== '' && { reason: reason.trim() }) },
    schema: BudgetDraft,
  });
}

export function editBudgetDraft(body: {
  fiscalYear: number;
  version: number;
  rows: { poolId: string; cashCents: number; options: number }[];
}): Promise<BudgetDraft> {
  return request('/api/v1/budgets/drafts', { method: 'PUT', body, schema: BudgetDraft });
}

export function confirmBudgetDraft(
  fiscalYear: number,
  version: number,
  reason: string,
): Promise<BudgetDraft> {
  return request('/api/v1/budgets/drafts/confirm', {
    method: 'POST',
    body: { fiscalYear, version, reason },
    schema: BudgetDraft,
  });
}

export function discardBudgetDraft(
  fiscalYear: number,
  version: number,
  reason: string,
): Promise<BudgetDraft> {
  return request('/api/v1/budgets/drafts/discard', {
    method: 'POST',
    body: { fiscalYear, version, reason },
    schema: BudgetDraft,
  });
}

/** Marks the carried-forward settings as looked at for the new year. */
export function reviewSettings(year: number, note: string): Promise<Rollover> {
  return request('/api/v1/admin/rollover/settings-review', {
    method: 'POST',
    body: { year, ...(note.trim() !== '' && { note: note.trim() }) },
    schema: Rollover,
  });
}

export function archiveYear(year: number, reason: string): Promise<YearStatus> {
  return request(`/api/v1/admin/years/${String(year)}/archive`, {
    method: 'POST',
    body: { reason },
    schema: YearStatus,
  });
}

export function unarchiveYear(year: number, reason: string): Promise<YearStatus> {
  return request(`/api/v1/admin/years/${String(year)}/unarchive`, {
    method: 'POST',
    body: { reason },
    schema: YearStatus,
  });
}
